/**
 * PasoServicioEngrase — Paso 6 del IS. Selector de tipo de servicio
 * (Engrase | Otro). Cuando es "engrase", muestra:
 *   - tiempoReposo (min/seg) — reemplaza frecuenciaLubricacion (REQ-10)
 *   - tiempoLubricacion (min/seg)
 *   - tipoPresion + presionTrabajo + presionLubricacion (fijos)
 *   - Zonas de engrase con TablaGenerica editable por zona (REQ-9, Commit 2)
 *
 * PR-D (ADR-D1): zona/inyector structure removed. Zones are user-managed.
 */
import { cn } from "@/shared/lib/utils.js";
import type { IsEngrase, ZonaEngrase } from "../../types.js";
import { labelClass } from "../ie/fields.js";
import { TablaGenerica } from "./TablaGenerica.js";
import {
  addZona,
  removeZona,
  setZonaTitulo,
  setZonaDescripcion,
  setZonaTabla,
} from "./zona-engrase-helpers.js";

// ── Helpers de parseo ─────────────────────────────────────────────────────────

/** Descompone "120 psi" → { valor: "120", unidad: "psi" }. */
function parseValorUnidad(s: string, defaultUnit: string): { valor: string; unidad: string } {
  const m = s.trim().match(/^([\d.,]*)[\s]*(.*)?$/);
  const valor = m?.[1] ?? "";
  const unidad = m?.[2]?.trim() || defaultUnit;
  return { valor, unidad };
}

function combinar(valor: string, unidad: string): string {
  return valor.trim() ? `${valor.trim()} ${unidad}` : "";
}

// ── Componente de campo numérico + unidad ─────────────────────────────────────

function NumericWithUnit(props: {
  id: string;
  label: string;
  value: string;
  units: string[];
  defaultUnit: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const { id, label, value, units, defaultUnit, onChange, disabled, placeholder } = props;
  const { valor, unidad } = parseValorUnidad(value, defaultUnit);
  const unidadActual = units.includes(unidad) ? unidad : defaultUnit;

  return (
    <div className="grid gap-1.5">
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      <div className="flex min-w-0 items-center gap-2">
        <input
          id={id}
          type="number"
          min="0"
          step="any"
          value={valor}
          disabled={disabled}
          placeholder={placeholder ?? "0"}
          onChange={(e) => onChange(combinar(e.target.value, unidadActual))}
          className={cn(
            "min-w-0 flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        />
        <select
          value={unidadActual}
          disabled={disabled}
          onChange={(e) => onChange(combinar(valor, e.target.value))}
          className={cn(
            "w-auto min-w-[4rem] shrink-0 rounded-md border border-input bg-background px-2 py-1.5 text-sm font-mono",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        >
          {units.map((u) => (
            <option key={u} value={u}>
              {u}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

// ── Unidades por contexto ─────────────────────────────────────────────────────

// ponytail: time units; ceiling = localized units or user-defined unit options
const UNIDADES_TIEMPO = ["seg", "min"];

const UNIDADES_PRESION: Record<"neumatico" | "hidraulico" | "electrico", string[]> = {
  neumatico: ["psi", "bar", "kPa"],
  hidraulico: ["bar", "MPa", "psi"],
  electrico: ["V", "kV"],
};
const UNIDADES_POTENCIA = ["kW", "W", "HP"];

const CAMPO_TRABAJO: Record<"neumatico" | "hidraulico" | "electrico", string> = {
  neumatico: "Presión de trabajo",
  hidraulico: "Presión de trabajo",
  electrico: "Voltaje de trabajo",
};
const CAMPO_LUBRICACION: Record<"neumatico" | "hidraulico" | "electrico", string> = {
  neumatico: "Presión de lubricación",
  hidraulico: "Presión de lubricación",
  electrico: "Potencia",
};

type TipoServicio = "engrase" | "otro";

const ZONA_VACIA: ZonaEngrase = { titulo: "", descripcion: "", tabla: { columnas: [], filas: [] } };

const ENGRASE_VACIO: IsEngrase = {
  tiempoReposo: "",
  tiempoLubricacion: "",
  tipoPresion: "neumatico",
  presionTrabajo: "",
  presionLubricacion: "",
  zonas: [],
};

export function PasoServicioEngrase(props: {
  tipoServicio: TipoServicio;
  engrase: IsEngrase | undefined;
  onTipoServicioChange: (next: TipoServicio) => void;
  onEngraseChange: (next: IsEngrase | undefined) => void;
  disabled?: boolean;
}) {
  const { tipoServicio, engrase, onTipoServicioChange, onEngraseChange, disabled } = props;

  const eng = engrase ?? ENGRASE_VACIO;

  function cambiarTipo(next: TipoServicio) {
    onTipoServicioChange(next);
    onEngraseChange(next === "engrase" ? (engrase ?? ENGRASE_VACIO) : undefined);
  }

  function setEng<K extends keyof IsEngrase>(key: K, v: IsEngrase[K]) {
    onEngraseChange({ ...eng, [key]: v });
  }

  return (
    <div className="space-y-8">
      {/* Selector segmentado de tipo de servicio */}
      <div className="grid gap-1.5">
        <span className={labelClass}>Tipo de servicio</span>
        <div
          role="radiogroup"
          aria-label="Tipo de servicio"
          className="inline-flex w-fit rounded-md border border-border bg-background p-1"
        >
          {(["engrase", "otro"] as const).map((opt) => {
            const active = tipoServicio === opt;
            return (
              <button
                key={opt}
                type="button"
                role="radio"
                aria-checked={active}
                disabled={disabled}
                onClick={() => cambiarTipo(opt)}
                className={cn(
                  "rounded px-4 py-1.5 font-mono text-xs uppercase tracking-[0.12em] transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                  active
                    ? "bg-brand-accent text-brand-accent-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {opt === "engrase" ? "Engrase" : "Otro"}
              </button>
            );
          })}
        </div>
      </div>

      {tipoServicio === "engrase" && (
        <>
          {/* Parámetros de lubricación — tiempo (REQ-10) */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <NumericWithUnit
              id="eng-tiempo-reposo"
              label="Tiempo de reposo"
              value={eng.tiempoReposo}
              units={UNIDADES_TIEMPO}
              defaultUnit="min"
              onChange={(v) => setEng("tiempoReposo", v)}
              disabled={disabled}
              placeholder="45"
            />
            <NumericWithUnit
              id="eng-tiempo-lubricacion"
              label="Tiempo de lubricación"
              value={eng.tiempoLubricacion}
              units={UNIDADES_TIEMPO}
              defaultUnit="seg"
              onChange={(v) => setEng("tiempoLubricacion", v)}
              disabled={disabled}
              placeholder="60"
            />
            <div className="grid gap-1.5">
              <label htmlFor="eng-tipo-presion" className={labelClass}>
                Tipo de presión
              </label>
              <select
                id="eng-tipo-presion"
                value={eng.tipoPresion}
                disabled={disabled}
                onChange={(e) => {
                  const v = e.target.value;
                  setEng(
                    "tipoPresion",
                    v === "hidraulico" ? "hidraulico" : v === "electrico" ? "electrico" : "neumatico",
                  );
                }}
                className={cn(
                  "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                )}
              >
                <option value="neumatico">Neumático</option>
                <option value="hidraulico">Hidráulico</option>
                <option value="electrico">Eléctrico</option>
              </select>
            </div>
            <NumericWithUnit
              id="eng-presion-trabajo"
              label={CAMPO_TRABAJO[eng.tipoPresion]}
              value={eng.presionTrabajo}
              units={UNIDADES_PRESION[eng.tipoPresion]}
              defaultUnit={UNIDADES_PRESION[eng.tipoPresion][0] ?? "bar"}
              onChange={(v) => setEng("presionTrabajo", v)}
              disabled={disabled}
              placeholder={eng.tipoPresion === "electrico" ? "220" : "80"}
            />
            <NumericWithUnit
              id="eng-presion-lubricacion"
              label={CAMPO_LUBRICACION[eng.tipoPresion]}
              value={eng.presionLubricacion}
              units={eng.tipoPresion === "electrico" ? UNIDADES_POTENCIA : UNIDADES_PRESION[eng.tipoPresion]}
              defaultUnit={eng.tipoPresion === "electrico" ? "kW" : (UNIDADES_PRESION[eng.tipoPresion][0] ?? "bar")}
              onChange={(v) => setEng("presionLubricacion", v)}
              disabled={disabled}
              placeholder={eng.tipoPresion === "electrico" ? "5.5" : "120"}
            />
          </div>

          {/* Zonas de engrase (REQ-9) */}
          <div className="space-y-4">
            <span className={labelClass}>Zonas de engrase</span>

            {eng.zonas.map((zona, i) => (
              <div key={i} className="rounded-lg border border-border p-4 space-y-4">
                {/* Zone header */}
                <div className="flex items-center justify-between">
                  <span className={labelClass}>Zona {i + 1}</span>
                  <button
                    type="button"
                    onClick={() => setEng("zonas", removeZona(eng.zonas, i))}
                    disabled={disabled}
                    className="text-destructive hover:text-destructive/80 text-sm"
                  >
                    Quitar zona
                  </button>
                </div>

                {/* Título input */}
                <div className="grid gap-1.5">
                  <label htmlFor={`titulo-zona-${i}`} className={labelClass}>
                    Título
                  </label>
                  <input
                    id={`titulo-zona-${i}`}
                    type="text"
                    value={zona.titulo}
                    onChange={(e) => setEng("zonas", setZonaTitulo(eng.zonas, i, e.target.value))}
                    disabled={disabled}
                    className={cn(
                      "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      "disabled:cursor-not-allowed disabled:opacity-50",
                    )}
                  />
                </div>

                {/* Descripción textarea */}
                <div className="grid gap-1.5">
                  <label htmlFor={`descripcion-zona-${i}`} className={labelClass}>
                    Descripción
                  </label>
                  <textarea
                    id={`descripcion-zona-${i}`}
                    value={zona.descripcion}
                    onChange={(e) => setEng("zonas", setZonaDescripcion(eng.zonas, i, e.target.value))}
                    disabled={disabled}
                    rows={2}
                    className={cn(
                      "flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      "disabled:cursor-not-allowed disabled:opacity-50",
                    )}
                  />
                </div>

                {/* Tabla editable */}
                <TablaGenerica
                  tabla={zona.tabla}
                  onChange={(t) => setEng("zonas", setZonaTabla(eng.zonas, i, t))}
                  disabled={disabled}
                />
              </div>
            ))}

            {/* Add zone button */}
            <button
              type="button"
              onClick={() => setEng("zonas", addZona(eng.zonas, ZONA_VACIA))}
              disabled={disabled}
              className={cn(
                "flex items-center gap-1.5 rounded-md border border-dashed border-border px-3 py-1.5 text-sm text-muted-foreground",
                "hover:border-foreground hover:text-foreground transition-colors",
                "disabled:cursor-not-allowed disabled:opacity-50",
              )}
            >
              + Agregar zona
            </button>
          </div>
        </>
      )}
    </div>
  );
}
